import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCalls,
  fetchLogs,
  fetchSummary,
  fetchTenants,
  getKey,
  isUnauthorized,
  openTail,
  resolveCall,
  setKey,
  type CallSummary,
  type TraceLine,
} from './api';
import { LogList } from './components/LogList';
import { StartupWaterfall } from './components/StartupWaterfall';
import { TurnLatency } from './components/TurnLatency';

type Theme = 'light' | 'dark';

const LEVELS = ['all', 'debug', 'info', 'warn', 'error'] as const;
type LevelFilter = (typeof LEVELS)[number];

const RANK: Record<string, number> = { debug: 10, info: 20, warn: 30, error: 40, fatal: 50 };

export default function App(): JSX.Element {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('logger-tool.theme') as Theme) ?? 'light',
  );
  const [tenant, setTenant] = useState('default');
  const [tenantOptions, setTenantOptions] = useState<string[]>([]);
  const [callId, setCallId] = useState('');
  const [activeCall, setActiveCall] = useState('');
  const [recent, setRecent] = useState<Array<{ conversationId: string; lastSeen: string }>>([]);

  const [lines, setLines] = useState<TraceLine[]>([]);
  const [summary, setSummary] = useState<CallSummary | null>(null);
  const [live, setLive] = useState(false);
  const [follow, setFollow] = useState(true);
  const [level, setLevel] = useState<LevelFilter>('all');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [keyInput, setKeyInput] = useState(getKey());
  // The read-key input used to live only in the sidebar, which is hidden below
  // 860px — so on a narrow window there was no way to enter a key at all and
  // the API just kept 401ing. This gates the whole view instead.
  const [needsKey, setNeedsKey] = useState(!getKey());

  const stopTail = useRef<null | (() => void)>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('logger-tool.theme', theme);
  }, [theme]);

  useEffect(() => {
    fetchTenants()
      .then((r) => {
        setNeedsKey(false);
        setTenantOptions(r.tenants);
        if (r.tenants.length && !r.tenants.includes(tenant)) setTenant(r.tenants[0]!);
      })
      .catch((e) => {
        if (isUnauthorized(e)) setNeedsKey(true);
      });
    // Tenants only change when a new one first sends data; once at mount is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshRecent = useCallback(() => {
    fetchCalls(tenant)
      .then((r) => setRecent(r.calls))
      .catch(() => setRecent([]));
  }, [tenant]);

  useEffect(refreshRecent, [refreshRecent]);

  const detach = useCallback(() => {
    stopTail.current?.();
    stopTail.current = null;
    setLive(false);
  }, []);

  // Always release the tail on unmount, or the server polls Redis for a tab
  // that no longer exists until its 15-minute cap.
  useEffect(() => detach, [detach]);

  const load = useCallback(
    async (id: string, tail: boolean) => {
      const trimmed = id.trim();
      if (!trimmed) return;
      detach();
      setNotice(null);
      setLines([]);
      setSummary(null);
      setActiveCall(trimmed);
      setFollow(true);

      // Whoever has a call id usually does not know its tenant, so find the
      // owner from the id rather than making them pick correctly first.
      let owner = tenant;
      try {
        const resolved = await resolveCall(trimmed);
        if (resolved.tenants.length === 1) {
          owner = resolved.tenants[0]!;
        } else if (resolved.tenants.length > 1) {
          // Ids are not guaranteed unique across tenants; say so rather than
          // silently showing one of them.
          owner = resolved.tenants.includes(tenant) ? tenant : resolved.tenants[0]!;
          setNotice(
            `This id exists in ${resolved.tenants.length} tenants (${resolved.tenants.join(', ')}) — showing ${owner}.`,
          );
        }
        if (owner !== tenant) setTenant(owner);
      } catch (e) {
        if (isUnauthorized(e)) {
          setNeedsKey(true);
          return;
        }
        // Resolution is a convenience; fall back to the selected tenant.
      }

      try {
        setSummary(await fetchSummary(owner, trimmed));
      } catch (e) {
        if (isUnauthorized(e)) setNeedsKey(true);
        setNotice((e as Error).message);
        return;
      }

      if (tail) {
        setLive(true);
        stopTail.current = openTail(owner, trimmed, undefined, {
          onBackfill: (batch) => setLines(batch),
          onLine: (line) => setLines((prev) => [...prev, line]),
          onEnded: (reason) => {
            setLive(false);
            if (reason === 'max-duration') setNotice('Live tail stopped after 15 minutes.');
          },
          onError: (message) => {
            setNotice(message);
            setLive(false);
          },
        });
      } else {
        try {
          const res = await fetchLogs(owner, trimmed);
          setLines(res.lines);
        } catch (e) {
          setNotice((e as Error).message);
        }
      }
      refreshRecent();
    },
    [tenant, detach, refreshRecent],
  );

  const visible = useMemo(() => {
    const min = level === 'all' ? 0 : (RANK[level] ?? 0);
    const q = search.trim().toLowerCase();
    return lines.filter((l) => {
      if ((RANK[(l.level ?? 'info').toLowerCase()] ?? 20) < min) return false;
      if (!q) return true;
      return (
        l.msg?.toLowerCase().includes(q) ||
        l.event?.toLowerCase().includes(q) ||
        JSON.stringify(l).toLowerCase().includes(q)
      );
    });
  }, [lines, level, search]);

  const saveKey = (): void => {
    setKey(keyInput.trim());
    setNotice(null);
    fetchTenants()
      .then((r) => {
        setNeedsKey(false);
        setTenantOptions(r.tenants);
        if (r.tenants.length) setTenant(r.tenants[0]!);
      })
      .catch((e) => {
        setNeedsKey(true);
        setNotice(isUnauthorized(e) ? 'That key was rejected.' : (e as Error).message);
      });
  };

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">CALL TRACE</span>

        {/* Free text, not a fixed dropdown: the tenant list is only whatever
            has shipped logs so far, and is resolved from the call id anyway. */}
        <input
          className="input"
          style={{ width: 130 }}
          list="tenant-options"
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          placeholder="tenant"
          title="Tenant — resolved automatically from the call id"
        />
        <datalist id="tenant-options">
          {tenantOptions.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>

        <input
          className="input input--grow"
          placeholder="conversation id"
          value={callId}
          onChange={(e) => setCallId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void load(callId, true);
          }}
        />
        <button className="btn btn--primary" onClick={() => void load(callId, true)}>
          Trace
        </button>
        <button className="btn" onClick={() => void load(callId, false)} title="History only">
          History
        </button>

        <span className="spacer" />

        <select
          className="select"
          value={level}
          onChange={(e) => setLevel(e.target.value as LevelFilter)}
          title="Minimum level"
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="filter"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className={follow ? 'btn btn--on' : 'btn'}
          onClick={() => setFollow((f) => !f)}
          title="Auto-scroll to newest"
        >
          Follow
        </button>
        {live && (
          <button className="btn" onClick={detach}>
            Stop
          </button>
        )}
        <button className="btn" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
          {theme === 'light' ? 'Dark' : 'Light'}
        </button>
      </header>

      {notice && <div className="notice">{notice}</div>}

      {needsKey ? (
        <div className="gate">
          <div className="gate__box">
            <h2 className="gate__title">Read key required</h2>
            <p className="gate__body">
              Call traces contain customer phone numbers and transcript fragments, so the API is
              key-gated. Paste the read key to continue — it is stored in this browser only.
            </p>
            <div className="gate__row">
              <input
                className="input"
                style={{ flex: 1, minWidth: 0 }}
                type="password"
                autoFocus
                placeholder="read key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveKey();
                }}
              />
              <button className="btn btn--primary" onClick={saveKey} disabled={!keyInput.trim()}>
                Unlock
              </button>
            </div>
          </div>
        </div>
      ) : (
      <div className="body">
        <aside className="sidebar">
          <div className="section-title">Recent calls</div>
          {recent.length === 0 && <div className="empty" style={{ padding: 20 }}>none</div>}
          {recent.map((c) => (
            <button
              key={c.conversationId}
              className={
                c.conversationId === activeCall ? 'call-item call-item--active' : 'call-item'
              }
              onClick={() => {
                setCallId(c.conversationId);
                void load(c.conversationId, true);
              }}
            >
              {c.conversationId}
              <span className="call-item__time">{new Date(c.lastSeen).toLocaleString()}</span>
            </button>
          ))}

          <div className="section-title">Read key</div>
          <div style={{ padding: '0 14px 16px', display: 'flex', gap: 6 }}>
            <input
              className="input"
              style={{ flex: 1, minWidth: 0 }}
              type="password"
              placeholder="optional"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
            />
            <button className="btn" onClick={saveKey}>
              Save
            </button>
          </div>
        </aside>

        <main className="main">
          {activeCall && (
            <div className="meta">
              <div className="meta__item">
                <span className="meta__label">Call</span>
                <span className="meta__value">{activeCall}</span>
              </div>
              <div className="meta__item">
                <span className="meta__label">Status</span>
                <span className="meta__value">
                  <span className={live ? 'dot dot--live' : 'dot'} />
                  {live ? 'live' : 'static'}
                </span>
              </div>
              <div className="meta__item">
                <span className="meta__label">Lines</span>
                <span className="meta__value">
                  {visible.length}
                  {visible.length !== lines.length ? ` / ${lines.length}` : ''}
                </span>
              </div>
              {summary && (
                <div className="meta__item">
                  <span className="meta__label">Expires in</span>
                  <span className="meta__value">
                    {Math.max(0, Math.round(summary.expiresInSeconds / 3600))}h
                  </span>
                </div>
              )}
            </div>
          )}

          <StartupWaterfall lines={lines} />
          <TurnLatency lines={lines} />

          {activeCall ? (
            <LogList lines={visible} follow={follow} onUnfollow={() => setFollow(false)} />
          ) : (
            <div className="empty">
              Paste a conversation id and press Trace, or pick a recent call.
            </div>
          )}
        </main>
      </div>
      )}
    </div>
  );
}
